import type { JsonMap, JSON } from './json'
import { evaluateExpr, parseExpr, ParsedExpr } from './expr'
import {
  Expression,
  evalExpression,
  isValueEmpty,
  contains,
  resolveValue,
  stringRepesentation,
  parseExpression
} from './expression'

export type ShowWhenContext = {
  inputs: JsonMap
  priorInputs: JsonMap
  settings: JsonMap
  context: JsonMap
}

type CompiledShowWhen =
  | { kind: 'expr'; parsed: ParsedExpr }
  | { kind: 'legacy'; parsed: Expression }

const showWhenCache = new Map<string, CompiledShowWhen | undefined>()

export function evalShowWhen(expr: string | undefined, context: ShowWhenContext) {
  if (!expr) return true
  const compiled = compileShowWhen(expr)
  if (!compiled) return false

  if (compiled.kind == 'legacy') {
    return evalExpression(compiled.parsed, {
      resolveValue: (path) => {
        if (path.startsWith('context.')) {
          return resolveValue(path.substring('context.'.length), context.context)
        }
        if (path.startsWith('prev_inputs.')) {
          return resolveValue(path.substring('prev_inputs.'.length), context.priorInputs)
        }
        if (path.startsWith('settings.')) {
          return resolveValue(path.substring('settings.'.length), context.settings)
        }
        return resolveValue(path, context.inputs)
      }
    })
  }

  const vars = {
    inputs: context.inputs,
    prev_inputs: context.priorInputs,
    settings: context.settings,
    context: context.context
  }

  return evaluateExpr(compiled.parsed, vars, defaultExprFns)
}

function compileShowWhen(expr: string | undefined): CompiledShowWhen | undefined {
  if (!expr) return undefined
  if (showWhenCache.has(expr)) {
    return showWhenCache.get(expr)
  }

  let compiled: CompiledShowWhen | undefined
  if (expr.includes('(')) {
    const parsed = parseExpr(expr)
    if (parsed) {
      compiled = { kind: 'expr', parsed }
    }
  }

  if (!compiled) {
    const parsed = parseExpression(expr)
    if (parsed) {
      compiled = { kind: 'legacy', parsed }
    }
  }

  showWhenCache.set(expr, compiled)
  return compiled
}

const defaultExprFns = {
  and: (...args: JSON[]) => args.every(Boolean),
  or: (...args: JSON[]) => args.some(Boolean),
  not: (value: JSON) => !value,
  eq: (left: JSON, right: JSON) => stringRepesentation(left) == stringRepesentation(right),
  neq: (left: JSON, right: JSON) => stringRepesentation(left) != stringRepesentation(right),
  get: (target: JSON, path: JSON) => {
    if (!path || typeof path != 'string') return undefined
    if (!target || typeof target != 'object' || Array.isArray(target)) return undefined
    return resolveValue(path, target as JsonMap)
  }
}
